import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Tag,
  Row,
  Col,
  Statistic,
  Spin,
  message,
  Button,
  Space
} from 'antd';
import {
  DollarOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  UpCircleOutlined
} from '@ant-design/icons';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

function PricingTable() {
  const navigate = useNavigate();
  const [pricing, setPricing] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });

  useEffect(() => {
    let isMounted = true;
    
    const loadData = async () => {
      if (isMounted) {
        await fetchPricing();
      }
    };
    
    loadData();
    
    return () => {
      isMounted = false;
    };
  }, []);

  const fetchPricing = async (page = 1, pageSize = 10) => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/pricing/?skip=${(page - 1) * pageSize}&limit=${pageSize}`);
      
      if (response.data && typeof response.data === 'object' && 'data' in response.data) {
        // Paginated response
        setPricing(response.data.data);
        setPagination(prev => ({
          ...prev,
          current: page,
          pageSize: pageSize,
          total: response.data.total
        }));
      } else {
        // Non-paginated response (fallback)
        setPricing(response.data);
        setPagination(prev => ({
          ...prev,
          current: page,
          pageSize: pageSize,
          total: response.data.length
        }));
      }
    } catch (error) {
      message.error('Failed to fetch pricing');
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND'
    }).format(price);
  };

  const formatCharacters = (chars) => {
    return new Intl.NumberFormat('vi-VN').format(chars);
  };

  const columns = [
    {
      title: 'Character Limit',
      dataIndex: 'character_limit',
      key: 'character_limit',
      render: (limit) => (
        <div style={{ textAlign: 'center' }}>
          <Statistic
            value={limit}
            formatter={(value) => `${formatCharacters(value)} characters`}
            valueStyle={{ color: '#1890ff', fontSize: '16px' }}
            prefix={<FileTextOutlined />}
          />
        </div>
      ),
      sorter: (a, b) => a.character_limit - b.character_limit,
    },
    {
      title: 'Price',
      dataIndex: 'price',
      key: 'price',
      render: (price) => (
        <div style={{ textAlign: 'center' }}>
          <Statistic
            value={price}
            formatter={(value) => formatPrice(value)}
            valueStyle={{ color: '#52c41a', fontSize: '18px', fontWeight: 'bold' }}
            prefix={<DollarOutlined />}
          />
        </div>
      ),
      sorter: (a, b) => a.price - b.price,
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      render: (description) => (
        <div style={{ textAlign: 'center' }}>
          <Tag color="blue" style={{ fontSize: '14px', padding: '4px 12px' }}>
            {description}
          </Tag>
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive) => (
        <div style={{ textAlign: 'center' }}>
          <Tag 
            color={isActive ? 'green' : 'red'}
            icon={isActive ? <CheckCircleOutlined /> : null}
            style={{ fontSize: '14px', padding: '4px 12px' }}
          >
            {isActive ? 'Available' : 'Unavailable'}
          </Tag>
        </div>
      ),
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => (
        <div style={{ textAlign: 'center' }}>
          <Button
            type="primary"
            icon={<UpCircleOutlined />}
            onClick={() => {
              navigate(`/payment-scanner/${record.id}`);
            }}
            disabled={!record.is_active}
            style={{ 
              background: record.is_active ? '#52c41a' : '#d9d9d9',
              borderColor: record.is_active ? '#52c41a' : '#d9d9d9'
            }}
          >
            Upgrade
          </Button>
        </div>
      ),
    },
  ];

  const totalPlans = (pricing || []).length;
  const activePlans = (pricing || []).filter(p => p.is_active).length;
  const minPrice = (pricing || []).length > 0 ? Math.min(...(pricing || []).map(p => p.price)) : 0;
  const maxPrice = (pricing || []).length > 0 ? Math.max(...(pricing || []).map(p => p.price)) : 0;

  return (
    <div>
      <div className="page-header">
        <h1>Pricing Plans</h1>
        <p>Choose the perfect plan for your text-to-voice needs</p>
      </div>

      {/* Statistics */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Total Plans"
              value={totalPlans}
              prefix={<FileTextOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Active Plans"
              value={activePlans}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Price Range"
              value={`${formatPrice(minPrice)} - ${formatPrice(maxPrice)}`}
              prefix={<DollarOutlined />}
              valueStyle={{ color: '#fa8c16' }}
            />
          </Card>
        </Col>
      </Row>

      <Card>
        <Spin spinning={loading}>
          <div className="table-responsive">
            <Table
              columns={columns}
              dataSource={pricing || []}
              rowKey="id"
              loading={loading}
              scroll={{ x: 600 }}
              pagination={{
                current: pagination.current,
                pageSize: pagination.pageSize,
                total: pagination.total,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} plans`,
                pageSizeOptions: ['10', '20', '50', '100', '200', '500', '1000'],
                onChange: (page, pageSize) => {
                  setPagination(prev => ({ ...prev, current: page, pageSize }));
                  fetchPricing(page, pageSize);
                },
                onShowSizeChange: (current, size) => {
                  setPagination(prev => ({ ...prev, current: 1, pageSize: size }));
                  fetchPricing(1, size);
                }
              }}
            />
          </div>
        </Spin>
      </Card>

    </div>
  );
}

export default PricingTable;